import { Avatar } from "antd";
import { EntityTypes, Facet } from "@vzbl/integrations";
import { useApplicationContext } from "src/hooks/useApplication";
import { SearchState } from "src/search/types";
import {
  useEntityContext,
  useEntitySearch,
  useEntityTimeSeries,
} from "../useEntityContext";
import { Insights } from "../Insights";
import { Identities, IdentitiesComponent } from "../Identities";
import { TimeSeries } from "../TimeSeries";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { MetricTag } from "./components/Tags";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { toHeaderAttributes } from "./components/HeaderAttributes";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { EntityTabs } from "./components/EntityTabs";
import { TabbedFilters } from "../components/TabbedFilters";
import { applySearchFilter, filterByEntity } from "../workstreams/common";
import { ReportsLinks } from "../components/Reports";
import { BetaTag } from "src/components/BetaTag";

export const PersonEntity = () => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entity = context.dataRef.current.entity.properties;
  const entityType = context.dataRef.current.entity.labels[0];

  const duration = "P30D";
  const messageTypes = [
    EntityTypes.Message,
    EntityTypes.CalendarEntry,
    EntityTypes.Comment,
    EntityTypes.IssueComment,
    EntityTypes.DiscussionComment,
    EntityTypes.PullRequestReviewComment,
  ];
  const workTimeline: SearchState = {
    orgId,
    duration,
    filters: [
      {
        attribute: Facet.Actor,
        comparison: {
          IN: [entity.name],
        },
        key: `${Facet.Actor}`,
        target: "Event",
      },
      {
        attribute: Facet.EntityType,
        comparison: {
          "NOT IN": messageTypes,
        },
        key: `${Facet.EntityType}`,
        target: "Event",
      },
    ],
  };
  const workTimelineContext = useEntityTimeSeries(workTimeline);
  const commTimeline: SearchState = {
    orgId,
    duration,
    filters: [
      {
        attribute: Facet.Actor,
        comparison: {
          IN: [entity.name],
        },
        key: `${Facet.Actor}`,
        target: "Event",
      },
      {
        attribute: Facet.EntityType,
        comparison: {
          IN: messageTypes,
        },
        key: `${Facet.EntityType}`,
        target: "Event",
      },
    ],
  };
  const commTimelineContext = useEntityTimeSeries(commTimeline);

  const { name, title, email, phone, timezone } = entity;

  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          label={name}
          entityType={title} // if available, otherwise defaults to entity type
          entity={context.dataRef.current.entity}
          attributes={toHeaderAttributes({
            orgId,
            context,
            entityType,
            attributes: [
              { name: "email", text: email || "N/A" },
              { name: "phone", text: phone || "N/A" },
              { name: "timezone", text: timezone || "N/A" },
            ],
          })}
          icon={
            <Avatar shape="circle" size="large" src={entity.avatarUrl}>
              {`${entity.name}`
                .split(" ")
                .map((name: string) => name[0].toLocaleUpperCase())
                .join("")}
            </Avatar>
          }
        />
      }
      summaryPanel={
        <SummaryPanel
          title={name}
          contentComponent={
            <Identities orgId={orgId} entityUuid={entity.uuid}>
              <strong style={{ fontWeight: 500, display: "inline-block" }}>
                Linked identities:
              </strong>
              <div
                style={{
                  display: "inline-block",
                  position: "relative",
                  top: 3,
                  paddingLeft: 16,
                }}
              >
                <IdentitiesComponent
                  style={{ display: "inline-flex", flexWrap: "wrap", gap: 16 }}
                />
              </div>
            </Identities>
          }
        />
      }
      insightsPanel={
        <InsightsPanel
          cards={[
            <ActionCard
              key="work-activity"
              badge={<MetricTag />}
              label="Work Activity"
              value={
                <TimeSeries
                  height={70}
                  shape="bar"
                  showXAxis={true}
                  searchContext={workTimelineContext}
                />
              }
            />,
            <ActionCard
              key="communication-activity"
              badge={<MetricTag />}
              label="Communication Activity"
              value={
                <TimeSeries
                  height={70}
                  shape="bar"
                  showXAxis={true}
                  searchContext={commTimelineContext}
                />
              }
            />,
          ]}
        />
      }
    >
      <EntityTabs
        propertiesSkipList={[
          "name",
          "title",
          "email",
          "phone",
          "timezone",
          "avatarUrl",
        ]}
        tabs={[
          {
            key: "overview",
            id: "overview",
            label: "Overview",
            children: <Insights entity={context.dataRef.current.entity} />,
          },
          {
            key: "reports",
            id: "reports",
            label: <>Reports</>,
            children: (
              <ReportsLinks
                filterable={EntityTypes.Person}
                filter={entity.name}
              />
            ),
          },
          {
            key: "communications",
            id: "communications",
            label: "Communications",
            children: (
              <TabbedFilters
                items={[
                  {
                    name: "All",
                    context: useEntitySearch(
                      applySearchFilter(
                        filterByEntity(
                          orgId,
                          //TODO reference from integrations.
                          [
                            EntityTypes.CalendarEntry,
                            EntityTypes.IssueComment,
                            EntityTypes.DiscussionComment,
                            EntityTypes.Comment,
                            EntityTypes.PullRequestReviewComment,
                            EntityTypes.Message,
                            EntityTypes.UserCallStatus,
                          ],
                          undefined,
                          duration
                        ),
                        [
                          {
                            attribute: Facet.Actor,
                            term: entity.name,
                          },
                        ]
                      )
                    ),
                  },
                  {
                    name: "Meetings",
                    context: useEntitySearch(
                      applySearchFilter(
                        filterByEntity(
                          orgId,
                          [EntityTypes.CalendarEntry],
                          undefined,
                          duration
                        ),
                        [
                          {
                            attribute: Facet.Actor,
                            term: entity.name,
                          },
                        ]
                      )
                    ),
                  },
                  {
                    name: "Comments",
                    context: useEntitySearch(
                      applySearchFilter(
                        filterByEntity(
                          orgId,
                          [
                            "IssueComment",
                            "DiscussionComment",
                            "Comment",
                            "PullRequestReviewComment",
                          ],
                          undefined,
                          duration
                        ),
                        [
                          {
                            attribute: Facet.Actor,
                            term: entity.name,
                          },
                        ]
                      )
                    ),
                  },
                  {
                    name: "Messages",
                    context: useEntitySearch(
                      applySearchFilter(
                        filterByEntity(
                          orgId,
                          [EntityTypes.Message],
                          undefined,
                          duration
                        ),
                        [
                          {
                            attribute: Facet.Actor,
                            term: entity.name,
                          },
                        ]
                      )
                    ),
                  },
                  {
                    name: "Huddles",
                    context: useEntitySearch(
                      applySearchFilter(
                        filterByEntity(
                          orgId,
                          [EntityTypes.UserCallStatus],
                          undefined,
                          duration
                        ),
                        [
                          {
                            attribute: Facet.Actor,
                            term: entity.name,
                          },
                        ]
                      )
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: "work",
            id: "work",
            label: "Work",
            children: (
              <Insights
                entity={context.dataRef.current.entity}
                insightName="PersonWork"
              />
            ),
          },
          {
            key: "time",
            id: "time",
            label: "Time",
            children: (
              <Insights
                entity={context.dataRef.current.entity}
                insightName="PersonTime"
              />
            ),
          },
        ]}
        entity={context.dataRef.current.entity}
      />
    </EntityScreen>
  );
};
